// components/canvas-surface/canvas-focus-view.js
// ═══════════════════════════════════════════════════════════
// Focus Mode components:
//   FocusBody  — toolbar + export + preview + version history
//   FocusView  — full-bleed artifact viewer/editor
//   ModeToggle — Read/Edit segmented control
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import CanvasToolbar from '../canvas/canvas-toolbar';
import CanvasVersionHistory from '../canvas/canvas-version-history';
import CanvasExportDropdown from '../canvas/canvas-export';
import CanvasArtifactPreview from '../canvas-artifact-preview';
import { isEditable } from '../../lib/canvas-modes';
import { classifyArtifact } from '../../lib/canvas-classify';
import { ModeToggleButton } from './canvas-gallery';

// ── Focus Body ──────────────────────────────────────────────
// Full Focus Mode layout: toolbar row + export dropdown +
// FocusView + version history side panel.
export function FocusBody({
  focusedNode,
  focusedArtifact,
  supabase,
  userRecord,
  transition,
  versionHistoryOpen,
  setVersionHistoryOpen,
  exportOpen,
  setExportOpen,
  onDuplicate,
  onRename,
}) {
  return (
    <div style={{
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      opacity: transition === 'focus-in' ? 0 : 1,
      transform: transition === 'focus-in' ? 'scale(0.97)' : 'scale(1)',
      transition: 'opacity 0.25s ease-out, transform 0.25s ease-out',
    }}>
      <CanvasToolbar
        onUndo={() => { /* TODO: wire Yjs undo manager */ }}
        onRedo={() => { /* TODO: wire Yjs undo manager */ }}
        canUndo={false}
        canRedo={false}
        onDuplicate={onDuplicate}
        onRename={onRename}
        title={focusedNode?.title || focusedArtifact?.title || 'Untitled'}
        readOnly={!focusedArtifact || focusedArtifact.artifact_type === 'image' || focusedArtifact.artifact_type === 'link'}
        onHistory={() => setVersionHistoryOpen(v => !v)}
        onExport={() => setExportOpen(v => !v)}
      />
      {/* Export dropdown — positioned relative to toolbar */}
      {exportOpen && (
        <div style={{ position: 'relative', zIndex: 25 }}>
          <CanvasExportDropdown
            artifact={focusedArtifact}
            visible={exportOpen}
            onClose={() => setExportOpen(false)}
          />
        </div>
      )}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        <FocusView
          node={focusedNode}
          artifact={focusedArtifact}
          supabase={supabase}
          userRecord={userRecord}
        />
        <CanvasVersionHistory
          artifactId={focusedArtifact?.id}
          supabase={supabase}
          visible={versionHistoryOpen}
          onClose={() => setVersionHistoryOpen(false)}
          onRestore={(content) => {
            setVersionHistoryOpen(false);
            window.dispatchEvent(new CustomEvent('workstation:artifact-updated', {
              detail: { artifactId: focusedArtifact?.id, content },
            }));
          }}
        />
      </div>
    </div>
  );
}

// ── Focus View ──────────────────────────────────────────
// Full-bleed artifact viewer/editor that replaces the tile grid when
// a node is focused. The artifact renders directly in the canvas panel
// — no modal, no overlay. Chat and journey map stay visible beside it.
export function FocusView({ node, artifact, supabase, userRecord }) {
  const kind = classifyArtifact(artifact, node);
  // HTML artifacts default to read mode (show the rendered design).
  // Doc and code default to edit mode (the canvas IS the work surface).
  // Read-only kinds (image, link, svg) stay in read mode.
  const [mode, setMode] = useState(
    kind === 'html' ? 'read' : isEditable(kind) ? 'edit' : 'read'
  );
  const showModeToggle = isEditable(kind);

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Sub-header with mode toggle + source indicator */}
      {showModeToggle && (
        <div style={{
          padding: '6px 14px',
          borderBottom: '1px solid #1e1e2a',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
          background: '#07070a',
        }}>
          <div style={{
            fontSize: 9,
            color: '#555',
            fontFamily: 'Georgia, serif',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}>
            {node.file_source === 'generated' ? 'Shift-generated' : 'Uploaded'}
          </div>
          <div style={{ flex: 1 }} />
          <ModeToggle mode={mode} onChange={setMode} />
        </div>
      )}
      {/* Artifact body — fills remaining canvas height with breathing room */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: (kind === 'html' || kind === 'svg' || kind === 'image') ? 10 : 0,
        background: '#07070a',
      }}>
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: (kind === 'html' || kind === 'svg' || kind === 'image') ? 6 : 0,
          border: (kind === 'html' || kind === 'svg' || kind === 'image') ? '1px solid #272736' : 'none',
        }}>
          <CanvasArtifactPreview
            artifact={artifact}
            node={node}
            kind={kind}
            mode={mode}
            supabase={supabase}
            userRecord={userRecord}
          />
        </div>
      </div>
    </div>
  );
}

// ── Read | Edit toggle ─────────────────────────────────────
// Two-button segmented control. Sage when active. Lives in the
// preview overlay header for editable artifact kinds (doc only
// in B1; more modes light up as their editors land).
export function ModeToggle({ mode, onChange }) {
  return (
    <div style={{
      display: 'inline-flex',
      border: '1px solid #2a2a2a',
      borderRadius: 4,
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      <ModeToggleButton label="Read" active={mode === 'read'} onClick={() => onChange('read')} />
      <ModeToggleButton label="Edit" active={mode === 'edit'} onClick={() => onChange('edit')} />
    </div>
  );
}
